import { ShaderProgram } from '../renderer/ShaderProgram';
import type { Renderable } from '../renderer/RenderPipeline';
import type { Camera } from '../renderer/Camera';
import type { ParsedMesh } from '../renderer/GlbParser';
import type { ShipTypeDef } from '../config/ShipTypes';

import vertSrc from '../shaders/ship_instanced.vert.glsl?raw';
import fragSrc from '../shaders/ship.frag.glsl?raw';

const FLOATS_PER_INSTANCE = 16;

/** Create a shared ship shader that multiple InstancedShipRenderers can reuse. */
export function createSharedShipShader(gl: WebGL2RenderingContext): ShaderProgram {
  return new ShaderProgram(gl, vertSrc, fragSrc);
}

/**
 * GPU-instanced renderer for a single ship type.
 * Pre-allocates instance buffer at init; fills per frame via updateInstances().
 */
export class InstancedShipRenderer implements Renderable {
  private _gl: WebGL2RenderingContext;
  private _shader: ShaderProgram;
  private _ownsShader: boolean;
  private _geoVao: WebGLVertexArrayObject | null = null;
  private _indexCount = 0;
  private _indexType: number;
  private _instanceBuf: WebGLBuffer;
  private _instanceData: Float32Array;
  private _instanceCount = 0;
  private _texture: WebGLTexture | null = null;
  private _color: readonly [number, number, number];
  private _ready = false;

  constructor(gl: WebGL2RenderingContext, private _typeDef: ShipTypeDef, maxInstances?: number, sharedShader?: ShaderProgram) {
    this._gl = gl;
    this._color = _typeDef.color;
    this._shader = sharedShader ?? new ShaderProgram(gl, vertSrc, fragSrc);
    this._ownsShader = !sharedShader;
    this._indexType = gl.UNSIGNED_SHORT;
    const cap = maxInstances ?? _typeDef.displayMax;
    this._instanceData = new Float32Array(cap * FLOATS_PER_INSTANCE);
    this._instanceBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._instanceBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this._instanceData.byteLength, gl.DYNAMIC_DRAW);
  }

  get typeDef(): ShipTypeDef { return this._typeDef; }

  async loadMesh(mesh: ParsedMesh): Promise<void> {
    const gl = this._gl;
    if (this._geoVao) gl.deleteVertexArray(this._geoVao);
    this._geoVao = gl.createVertexArray()!;
    gl.bindVertexArray(this._geoVao);

    this._uploadGeoAttr(0, mesh.positions, 3);
    if (mesh.normals) {
      this._uploadGeoAttr(1, mesh.normals, 3);
    } else {
      const fb = new Float32Array(mesh.positions.length);
      for (let i = 1; i < fb.length; i += 3) fb[i] = 1.0;
      this._uploadGeoAttr(1, fb, 3);
    }
    if (mesh.uvs) this._uploadGeoAttr(2, mesh.uvs, 2);

    if (mesh.indices) {
      const idxBuf = gl.createBuffer()!;
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuf);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);
      this._indexCount = mesh.indices.length;
      this._indexType = mesh.indices instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
    } else {
      this._indexCount = mesh.positions.length / 3;
      this._indexType = 0;
    }

    this._setupInstanceAttrs(gl);
    gl.bindVertexArray(null);

    if (mesh.baseColorImage) await this._loadTex(mesh.baseColorImage);
    this._ready = true;
  }

  updateInstances(data: Float32Array, count: number): void {
    this._instanceCount = count;
    if (count === 0) return;
    const gl = this._gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._instanceBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, count * FLOATS_PER_INSTANCE);
  }

  render(gl: WebGL2RenderingContext, camera: Camera, time = 0): void {
    if (!this._ready || this._instanceCount === 0 || !this._geoVao) return;

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setVec3('u_color', this._color[0], this._color[1], this._color[2]);
    this._shader.setFloat('u_time', time);

    if (this._texture) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._texture);
      this._shader.setInt('u_texture', 0);
      this._shader.setFloat('u_hasTexture', 1.0);
    } else {
      this._shader.setFloat('u_hasTexture', 0.0);
    }

    gl.bindVertexArray(this._geoVao);
    if (this._indexType === 0) {
      gl.drawArraysInstanced(gl.TRIANGLES, 0, this._indexCount, this._instanceCount);
    } else {
      gl.drawElementsInstanced(
        gl.TRIANGLES, this._indexCount, this._indexType, 0, this._instanceCount,
      );
    }
    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this._gl;
    if (this._geoVao) gl.deleteVertexArray(this._geoVao);
    gl.deleteBuffer(this._instanceBuf);
    if (this._texture) gl.deleteTexture(this._texture);
    if (this._ownsShader) this._shader.dispose();
  }

  private _uploadGeoAttr(loc: number, data: Float32Array, size: number): void {
    const gl = this._gl;
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
  }

  private _setupInstanceAttrs(gl: WebGL2RenderingContext): void {
    gl.bindBuffer(gl.ARRAY_BUFFER, this._instanceBuf);
    const stride = FLOATS_PER_INSTANCE * 4;
    for (let col = 0; col < 4; col++) {
      const loc = 3 + col;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, stride, col * 16);
      gl.vertexAttribDivisor(loc, 1);
    }
  }

  private async _loadTex(img: { bytes: Uint8Array; mimeType: string }): Promise<void> {
    const blob = new Blob([img.bytes.slice()], { type: img.mimeType });
    const bmp = await createImageBitmap(blob);
    const gl = this._gl;
    this._texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    bmp.close();
  }
}
